import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Users, Mail, MailX, FileText, Search, LogOut, Save, X, Download, UserPlus, Flame, FlameKindling, Bell, BellOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState("registrations");
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState("");
  const [newsletterContent, setNewsletterContent] = useState("");
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterResult, setNewsletterResult] = useState(null);
  const [promoActive, setPromoActive] = useState(false);
  const [promoToggling, setPromoToggling] = useState(false);
  const [notifyEnabled, setNotifyEnabled] = useState(() => {
    try { return localStorage.getItem('sks_notify') === '1'; } catch (_) { return false; }
  });
  const [notifyPermission, setNotifyPermission] = useState(() => {
    try { return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'; } catch (_) { return 'unsupported'; }
  });
  const lastKnownIdsRef = useRef(new Set());
  const pollIntervalRef = useRef(null);

  // Toggle browser notifications
  const handleToggleNotifications = async () => {
    if (typeof Notification === 'undefined') {
      alert("Il tuo browser non supporta le notifiche. Usa Chrome/Edge/Firefox su desktop o Android.");
      return;
    }
    if (notifyEnabled) {
      setNotifyEnabled(false);
      try { localStorage.setItem('sks_notify', '0'); } catch (_) { /* ignore */ }
      return;
    }
    let perm = Notification.permission;
    if (perm === 'default') {
      perm = await Notification.requestPermission();
      setNotifyPermission(perm);
    }
    if (perm === 'granted') {
      setNotifyEnabled(true);
      try { localStorage.setItem('sks_notify', '1'); } catch (_) { /* ignore */ }
      // Show test notification
      new Notification('🏄 Notifiche attivate!', {
        body: 'Riceverai un avviso ad ogni nuova iscrizione.',
        icon: '/favicon.ico',
        tag: 'sks-test',
      });
    } else {
      alert("Permesso negato. Per ricevere notifiche, abilita i permessi dal lucchetto in alto a sinistra nel browser.");
    }
  };

  // Polling for new registrations
  useEffect(() => {
    if (!isLoggedIn || !notifyEnabled) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const checkNew = async () => {
      try {
        const res = await fetch(`${API}/admin/registrations`);
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data)) return;
        const currentIds = new Set(data.map(r => r.id));

        // First load: just record IDs, no notification (avoid spamming on first poll)
        if (lastKnownIdsRef.current.size === 0) {
          lastKnownIdsRef.current = currentIds;
          return;
        }

        // Find new IDs
        const newOnes = data.filter(r => !lastKnownIdsRef.current.has(r.id));
        if (newOnes.length > 0 && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          newOnes.forEach(reg => {
            try {
              const notif = new Notification('🏄 Nuova iscrizione!', {
                body: `${reg.name || ''} ${reg.surname || ''} — ${(reg.lesson_type || '').replace(/_/g, ' ')}`,
                icon: '/favicon.ico',
                tag: `sks-new-${reg.id}`,
                requireInteraction: false,
              });
              notif.onclick = () => {
                window.focus();
                notif.close();
              };
            } catch (_) { /* ignore */ }
          });
          // Play sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAEAAAAAAAA/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AA==');
            audio.volume = 0.4;
            audio.play().catch(() => {});
          } catch (_) { /* ignore */ }
        }
        lastKnownIdsRef.current = currentIds;
      } catch (_) { /* ignore */ }
    };

    // Run immediately, then every 30s
    checkNew();
    pollIntervalRef.current = setInterval(checkNew, 30000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [isLoggedIn, notifyEnabled]);

  // Fetch promo status when logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    fetch(`${API}/promo/status`)
      .then(r => r.json())
      .then(d => setPromoActive(!!d?.active))
      .catch(() => {});
  }, [isLoggedIn]);

  const handleTogglePromo = async () => {
    const next = !promoActive;
    const confirmMsg = next
      ? "Attivare la promo NOWIND LAST MINUTES? La pagina /promo sarà visibile a tutti."
      : "Disattivare la promo NOWIND LAST MINUTES? La pagina /promo mostrerà 'Promo non attiva'.";
    if (!window.confirm(confirmMsg)) return;
    setPromoToggling(true);
    try {
      const res = await fetch(`${API}/admin/promo/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: next }),
      });
      const data = await res.json();
      setPromoActive(!!data.active);
    } catch (e) {
      alert("Errore durante il toggle promo");
    } finally {
      setPromoToggling(false);
    }
  };

  // Check if already logged in - initialize state lazily
  const [hasCheckedToken, setHasCheckedToken] = useState(false);
  if (!hasCheckedToken) {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem("sks_admin_token") : null;
    if (token && !isLoggedIn) {
      setIsLoggedIn(true);
    }
    setHasCheckedToken(true);
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem("sks_admin_token", data.token);
        setIsLoggedIn(true);
        loadData();
      } else {
        setLoginError("Credenziali non valide");
      }
    } catch (err) {
      setLoginError("Errore di connessione");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("sks_admin_token");
    setIsLoggedIn(false);
    setRegistrations([]);
    setStats(null);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await fetch(`${API}/admin/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      let url = `${API}/admin/registrations`;
      if (filter === "newsletter_yes") url += "?newsletter=yes";
      else if (filter === "newsletter_no") url += "?newsletter=no";
      
      const regRes = await fetch(url);
      if (regRes.ok) {
        const regData = await regRes.json();
        setRegistrations(regData);
      }

      const membersRes = await fetch(`${API}/admin/members`);
      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setMembers(membersData);
      }
    } catch (err) {
      console.error("Error loading data:", err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    if (isLoggedIn) {
      loadData();
    }
  }, [isLoggedIn, loadData]);

  const saveNote = async () => {
    if (!selectedRegistration) return;
    
    try {
      const res = await fetch(`${API}/admin/registrations/${selectedRegistration.id}/note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: noteText })
      });
      
      if (res.ok) {
        // Update local state
        setRegistrations(prev => prev.map(r => 
          r.id === selectedRegistration.id 
            ? { ...r, admin_note: noteText }
            : r
        ));
        setSelectedRegistration(null);
        setNoteText("");
      }
    } catch (err) {
      console.error("Error saving note:", err);
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.name?.toLowerCase().includes(term) ||
      r.surname?.toLowerCase().includes(term) ||
      r.email?.toLowerCase().includes(term) ||
      r.phone?.includes(term)
    );
  });

  const filteredMembers = members.filter(m => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      m.name?.toLowerCase().includes(term) ||
      m.surname?.toLowerCase().includes(term) ||
      m.email?.toLowerCase().includes(term) ||
      m.phone?.includes(term)
    );
  });

  const handleDeleteMember = async (id, name) => {
    if (!window.confirm(`Sei sicuro di voler cancellare l'iscrizione di ${name}?`)) return;
    try {
      const res = await fetch(`${API}/admin/members/${id}`, { method: "DELETE" });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.id !== id));
      }
    } catch (err) {
      console.error("Delete member error:", err);
    }
  };

  const saveMemberNote = async () => {
    if (!selectedRegistration) return;
    try {
      const res = await fetch(`${API}/admin/members/${selectedRegistration.id}/note`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: noteText })
      });
      if (res.ok) {
        setMembers(prev => prev.map(m => m.id === selectedRegistration.id ? { ...m, admin_note: noteText } : m));
        setSelectedRegistration(null);
        setNoteText("");
      }
    } catch (err) {
      console.error("Error saving member note:", err);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterContent.trim()) return;
    setSendingNewsletter(true);
    setNewsletterResult(null);
    try {
      const res = await fetch(`${API}/admin/send-newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: newsletterSubject, html_content: newsletterContent })
      });
      const data = await res.json();
      setNewsletterResult(data);
      if (data.success && data.sent > 0) {
        setNewsletterSubject("");
        setNewsletterContent("");
      }
    } catch (err) {
      setNewsletterResult({ success: false, message: "Errore di rete" });
    }
    setSendingNewsletter(false);
  };

  const handleExportExcel = () => {
    window.open(`${API}/admin/export-excel`, '_blank');
  };

  const handleDeleteRegistration = async (id, name) => {
    if (!window.confirm(`Sei sicuro di voler cancellare l'iscrizione di ${name}?`)) return;
    try {
      const res = await fetch(`${API}/admin/registrations/${id}`, { method: "DELETE" });
      if (res.ok) {
        setRegistrations(prev => prev.filter(r => r.id !== id));
        loadData();
      }
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 to-cyan-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Admin SKS</h1>
            <p className="text-gray-500 text-sm mt-1">Sardinia Kite School</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Username"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                data-testid="admin-password-input"
              />
            </div>
            
            {loginError && (
              <p className="text-red-500 text-sm text-center">{loginError}</p>
            )}
            
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">🏄‍♀️ Admin SKS</h1>
            <p className="text-teal-100 text-sm">Gestione Iscrizioni</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleToggleNotifications}
              variant="ghost"
              className={`text-white hover:bg-white/20 ${notifyEnabled ? 'bg-emerald-500/30 ring-1 ring-emerald-300' : ''}`}
              data-testid="admin-notify-toggle"
              title={notifyEnabled ? "Notifiche ATTIVE - Clicca per disattivare" : "Notifiche disattivate - Clicca per attivare"}
            >
              {notifyEnabled ? <Bell className="w-4 h-4 mr-2 text-emerald-300 animate-pulse" /> : <BellOff className="w-4 h-4 mr-2" />}
              Notifiche {notifyEnabled ? "ON" : "OFF"}
            </Button>
            <Button
              onClick={handleTogglePromo}
              disabled={promoToggling}
              variant="ghost"
              className={`text-white hover:bg-white/20 ${promoActive ? 'bg-orange-500/30 ring-1 ring-orange-300' : ''}`}
              data-testid="admin-promo-toggle"
              title={promoActive ? "Promo NOWIND ATTIVA - Clicca per disattivare" : "Promo NOWIND DISATTIVATA - Clicca per attivare"}
            >
              {promoActive ? <Flame className="w-4 h-4 mr-2 text-orange-300" /> : <FlameKindling className="w-4 h-4 mr-2" />}
              Promo {promoActive ? "ON" : "OFF"}
            </Button>
            <Button
              onClick={handleExportExcel}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              onClick={() => setShowNewsletter(!showNewsletter)}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="newsletter-toggle-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Newsletter
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white/20"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_registrations}</p>
                  <p className="text-xs text-gray-500">Totale Iscritti</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.newsletter_subscribers}</p>
                  <p className="text-xs text-gray-500">Newsletter Sì</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <MailX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.newsletter_not_subscribed}</p>
                  <p className="text-xs text-gray-500">Newsletter No</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.media_consent_given}</p>
                  <p className="text-xs text-gray-500">Consenso Media</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab("registrations")}
            variant={activeTab === "registrations" ? "default" : "outline"}
            className={activeTab === "registrations" ? "bg-teal-600 hover:bg-teal-700" : ""}
            data-testid="tab-registrations"
          >
            <FileText className="w-4 h-4 mr-2" />
            Iscrizioni ({registrations.length})
          </Button>
          <Button
            onClick={() => setActiveTab("members")}
            variant={activeTab === "members" ? "default" : "outline"}
            className={activeTab === "members" ? "bg-orange-600 hover:bg-orange-700" : ""}
            data-testid="tab-members"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Iscrizione Diretta ({members.length})
          </Button>
        </div>

        {/* Newsletter Panel */}
        <AnimatePresence>
          {showNewsletter && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6 overflow-hidden"
            >
              <h2 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Invia Newsletter
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Invia email solo a chi ha accettato la newsletter ({stats?.newsletter_subscribers || 0} iscritti).
                Usa <code className="bg-gray-100 px-1 rounded">{"{name}"}</code> per personalizzare con il nome.
              </p>
              <div className="space-y-3">
                <Input
                  placeholder="Oggetto email..."
                  value={newsletterSubject}
                  onChange={(e) => setNewsletterSubject(e.target.value)}
                  data-testid="newsletter-subject"
                />
                <Textarea
                  placeholder="Contenuto email (HTML supportato)..."
                  value={newsletterContent}
                  onChange={(e) => setNewsletterContent(e.target.value)}
                  rows={6}
                  data-testid="newsletter-content"
                />
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSendNewsletter}
                    disabled={sendingNewsletter || !newsletterSubject.trim() || !newsletterContent.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="send-newsletter-btn"
                  >
                    {sendingNewsletter ? "Invio in corso..." : "Invia a tutti gli iscritti"}
                  </Button>
                  <Button
                    onClick={() => { setShowNewsletter(false); setNewsletterResult(null); }}
                    variant="outline"
                  >
                    Chiudi
                  </Button>
                </div>
                {newsletterResult && (
                  <div className={`text-sm p-3 rounded-lg ${newsletterResult.success && newsletterResult.sent > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                    {newsletterResult.success
                      ? `Email inviate: ${newsletterResult.sent}/${newsletterResult.total_subscribers}`
                      : newsletterResult.message || "Nessun iscritto alla newsletter"}
                    {newsletterResult.errors?.length > 0 && (
                      <p className="mt-1 text-xs">Errori: {newsletterResult.errors.join(", ")}</p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {activeTab === "registrations" && (
        <>
        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex gap-2">
              <Button
                onClick={() => setFilter("all")}
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                data-testid="filter-all-button"
              >
                Tutti
              </Button>
              <Button
                onClick={() => setFilter("newsletter_yes")}
                variant={filter === "newsletter_yes" ? "default" : "outline"}
                size="sm"
                className={filter === "newsletter_yes" ? "bg-green-600 hover:bg-green-700" : ""}
                data-testid="filter-newsletter-yes-button"
              >
                <Mail className="w-4 h-4 mr-1" />
                Newsletter Sì
              </Button>
              <Button
                onClick={() => setFilter("newsletter_no")}
                variant={filter === "newsletter_no" ? "default" : "outline"}
                size="sm"
                className={filter === "newsletter_no" ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="filter-newsletter-no-button"
              >
                <MailX className="w-4 h-4 mr-1" />
                Newsletter No
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Cerca per nome, email o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Registrations List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lezione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">📧</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">📸</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      Caricamento...
                    </td>
                  </tr>
                ) : filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                      Nessuna iscrizione trovata
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((reg) => (
                    <tr key={reg.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{reg.name} {reg.surname}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{reg.email}</div>
                        <a 
                          href={`https://wa.me/${reg.phone?.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-green-600 hover:underline"
                        >
                          {reg.phone}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">
                          {reg.lesson_type}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">{reg.duration}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{reg.preferred_date || '-'}</div>
                        <div className="text-xs text-gray-400">{reg.preferred_time || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {reg.accepts_newsletter ? (
                          <span className="text-green-600 text-lg">✓</span>
                        ) : (
                          <span className="text-red-400 text-lg">✗</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {reg.accepts_media_consent ? (
                          <span className="text-green-600 text-lg">✓</span>
                        ) : (
                          <span className="text-red-400 text-lg">✗</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-xs truncate text-sm text-gray-600">
                          {reg.admin_note || <span className="text-gray-400 italic">Nessuna nota</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`note-btn-${reg.id}`}
                            onClick={() => {
                              setSelectedRegistration(reg);
                              setNoteText(reg.admin_note || "");
                            }}
                          >
                            ✏️ Nota
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`download-pdf-btn-${reg.id}`}
                            className="text-teal-700 border-teal-300 hover:bg-teal-50"
                            onClick={() => {
                              window.open(`${API}/admin/registrations/${reg.id}/pdf`, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`delete-btn-${reg.id}`}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteRegistration(reg.id, `${reg.name} ${reg.surname}`)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}

        {/* Members (Direct Registration) Tab */}
        {activeTab === "members" && (
        <>
        {/* Search */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Cerca per nome, email o telefono..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="member-search-input"
                />
              </div>
            </div>
            <div className="text-sm text-gray-500 whitespace-nowrap">
              Link iscrizione: <a href="/member" target="_blank" rel="noopener noreferrer" className="text-teal-600 font-medium hover:underline">/member</a>
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
            <h2 className="font-bold text-orange-800 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Iscrizione Diretta ({filteredMembers.length})
            </h2>
            <p className="text-xs text-orange-600 mt-1">Iscrizioni compilate direttamente dal link /member (senza attivita/durata/data)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contatto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cod. Fiscale</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Nascita</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">NL</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Media</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Caricamento...</td></tr>
                ) : filteredMembers.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">Nessuna iscrizione diretta trovata</td></tr>
                ) : (
                  filteredMembers.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{m.name} {m.surname}</div>
                        <div className="text-xs text-gray-400">{m.created_at ? new Date(m.created_at).toLocaleDateString('it-IT') : ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">{m.email}</div>
                        <a href={`https://wa.me/${m.phone?.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline">{m.phone}</a>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.fiscal_code || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{m.birth_date || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        {m.accepts_newsletter ? <span className="text-green-600 text-lg">&#10003;</span> : <span className="text-red-400 text-lg">&#10007;</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {m.accepts_media_consent ? <span className="text-green-600 text-lg">&#10003;</span> : <span className="text-red-400 text-lg">&#10007;</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-xs truncate text-sm text-gray-600">
                          {m.admin_note || <span className="text-gray-400 italic">Nessuna nota</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center flex-wrap">
                          <Button size="sm" variant="outline" data-testid={`member-note-btn-${m.id}`}
                            onClick={() => { setSelectedRegistration({...m, _isMember: true}); setNoteText(m.admin_note || ""); }}>
                            Nota
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`member-pdf-btn-${m.id}`}
                            className="text-teal-700 border-teal-300 hover:bg-teal-50"
                            onClick={() => window.open(`${API}/admin/members/${m.id}/pdf`, '_blank')}>
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`member-delete-btn-${m.id}`}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteMember(m.id, `${m.name} ${m.surname}`)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Note Modal */}
      <AnimatePresence>
        {selectedRegistration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedRegistration(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  Nota per {selectedRegistration.name} {selectedRegistration.surname}
                </h3>
                <button
                  onClick={() => setSelectedRegistration(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Scrivi una nota per questa prenotazione..."
                rows={4}
                className="mb-4"
              />
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setSelectedRegistration(null)}
                >
                  Annulla
                </Button>
                <Button
                  onClick={selectedRegistration?._isMember ? saveMemberNote : saveNote}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salva
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
